import { Locales } from '@griply/types';
import { useCallback, useMemo, useState } from 'react';
import { IconFormNames } from 'shared/assets/icons';
import { useToday } from 'shared/contexts/today';
import { useNlpRegex } from 'shared/hooks/use-nlp-regex';
import { useI18nLocale } from 'shared/lib/react-i18next/use-i18n-locale';
import {
  DateFormatOptions,
  TimeFormatOptions,
} from 'shared/types/date-format-options';
import { Goal, NewGoalWithID } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { Person } from 'shared/types/nlp';
import { NewTagWithID, Tag } from 'shared/types/tag';
import { CreateTask, NewSubTask } from 'shared/types/task';
import { TaskType } from 'shared/types/task-base';
import { Timestamp } from 'shared/types/timestamp';
import { buildCreateTaskFromProperties } from 'shared/utils/nlp/build-create-task-from-properties';
import { WordId } from 'shared/utils/nlp/get-ids-for-words';
import { processNewTaskVariablesInString } from 'shared/utils/nlp/process-new-task-variables-in-string';

export type Options = {
  nlpString: string;
  updateNlpString: (name: string) => void;
  lifeAreas: LifeArea[];
  goals: (Goal | NewGoalWithID)[];
  tags: (Tag | NewTagWithID)[];
  people?: Person[];
  language: Locales;
  dateFormat: DateFormatOptions;
  timeFormat: TimeFormatOptions;
  createHabitAllowed: boolean;
  scheduleTargetAllowed: boolean;
  dateAnchored?: boolean;
};

export type UpdateTaskVariablesOptions = {
  newTags?: (Tag | NewTagWithID)[];
};

export const useNewTaskState = (
  initialProperties: Partial<CreateTask> = {},
  {
    nlpString,
    updateNlpString,
    lifeAreas,
    goals,
    tags,
    people,
    language,
    dateFormat,
    timeFormat,
    createHabitAllowed,
    scheduleTargetAllowed,
    dateAnchored,
  }: Options,
) => {
  const today = useToday();
  const locale = useI18nLocale();
  const [description, updateDescription] = useState('');
  const [subTasks, setSubTasks] = useState<NewSubTask[]>([]);
  const [changedProps, setChangedProps] = useState<(keyof CreateTask)[]>([]);
  const [unfinishedSubtask, setUnfinishedSubtask] = useState<string>('');
  const [hideOnHabitList, setHideOnHabitList] = useState(!createHabitAllowed);
  const [hideOnTodo, setHideOnTodo] = useState(false);
  const [completionsPerDay, setCompletionsPerDay] = useState(1);
  const [scheduleEndDate, setScheduleEndDate] = useState<Timestamp | null>(
    null,
  );
  const [iconName, setIconName] = useState<IconFormNames>(IconFormNames.Repeat);

  const [ignoredWords, updateIgnoredWords] = useState<ID[]>([]);
  const [wordIds, updateWordIds] = useState<WordId[]>([]);

  const nlpRegex = useNlpRegex({ language, people });

  const task = useMemo(
    () =>
      buildCreateTaskFromProperties({
        initialProperties,
        changedProps,
        nlpString,
        nlpRegex,
        wordIds,
        ignoredWordIds: ignoredWords,
        language,
        dateFormat,
        createHabitAllowed,
        scheduleTargetAllowed,
        dateAnchored,
        description,
        iconName,
        hideOnTodo,
        hideOnHabitList,
        completionsPerDay,
        scheduleEndDate,
        goals,
        lifeAreas,
        tags,
        today,
      }),
    [
      changedProps,
      completionsPerDay,
      createHabitAllowed,
      dateAnchored,
      dateFormat,
      description,
      goals,
      hideOnHabitList,
      hideOnTodo,
      iconName,
      ignoredWords,
      initialProperties,
      language,
      lifeAreas,
      nlpRegex,
      nlpString,
      scheduleEndDate,
      scheduleTargetAllowed,
      tags,
      today,
      wordIds,
    ],
  );

  const updateTaskVariables = useCallback(
    (
      taskVariables: Partial<CreateTask>,
      { newTags }: UpdateTaskVariablesOptions = {},
    ) => {
      setChangedProps((prev) =>
        Array.from(
          new Set<keyof CreateTask>([
            ...prev,
            ...(Object.keys(taskVariables) as (keyof CreateTask)[]),
          ]),
        ),
      );

      // Tokens are written by name, so a tag created in this same call is not in `tags` yet.
      const unlistedTags = (newTags ?? []).filter(
        ({ id }) => !tags.some((tag) => tag.id === id),
      );

      updateNlpString(
        processNewTaskVariablesInString(nlpString, taskVariables, {
          goals,
          lifeAreas,
          tags: unlistedTags.length ? [...unlistedTags, ...tags] : tags,
          language: locale,
          dateFormat,
          timeFormat,
          wordIds,
          ignoredWordIds: ignoredWords,
          createHabitAllowed,
          today,
        }),
      );
      if ('description' in taskVariables) {
        updateDescription(taskVariables.description ?? '');
      }
      if ('hideOnTodo' in taskVariables) {
        setHideOnTodo(!!taskVariables.hideOnTodo);
      }
      if (taskVariables.type === TaskType.Repeating) {
        setHideOnHabitList(true);
      }
      if (taskVariables.type === TaskType.Habit) {
        setHideOnHabitList(false);
      }
      if ('schedules' in taskVariables) {
        const schedule = taskVariables.schedules?.[0];
        if (schedule && schedule.rrule) {
          setCompletionsPerDay(schedule.completionTarget.count);
        }
        setScheduleEndDate(schedule?.endDate ?? null);
      }
      if ('iconName' in taskVariables) {
        setIconName(taskVariables.iconName ?? IconFormNames.Repeat);
      }
    },
    [
      createHabitAllowed,
      dateFormat,
      goals,
      locale,
      ignoredWords,
      lifeAreas,
      nlpString,
      tags,
      timeFormat,
      today,
      updateNlpString,
      wordIds,
    ],
  );

  const reset = useCallback(() => {
    setChangedProps([]);
    updateDescription('');
    setHideOnTodo(false);
    setHideOnHabitList(!createHabitAllowed);
    setCompletionsPerDay(1);
    setScheduleEndDate(null);
    setSubTasks([]);
    setUnfinishedSubtask('');
  }, [createHabitAllowed]);

  return {
    updateWordIds,
    updateIgnoredWords,
    task,
    subTasks,
    updateSubTasks: setSubTasks,
    newSubTask: unfinishedSubtask,
    updateNewSubTask: setUnfinishedSubtask,
    reset,
    updateTaskVariables,
  };
};
